/**
 * api.ts — Auth client for the ɳSentry SaaS app zone
 *
 * Purpose:    Cookie-auth client mirroring web/ntask's pattern (login/logout/session
 *             Vercel Functions). Adds a MOCK MODE so the app zone is fully demoable
 *             before the SaaS backend box (W2) is live.
 * Inputs:     email/password (signIn/signUp); nothing (getUser/signOut)
 * Outputs:    ApiResponse<T> discriminated on { data } | { error }
 * Constraints: Browser-only (Vite SPA, no SSR). Mock session persists to
 *              localStorage key `nsentry_mock_session` so a reload keeps you signed in.
 * SOT:        nsentry SaaS auth zone
 */

export interface ApiResponse<T> {
  data?: T
  error?: {
    message: string
    code?: string
    status?: number
  }
}

export interface AuthUser {
  id: string
  email: string
  displayName?: string
}

import { setSaasToken } from './saas-token'

const MOCK_MODE = import.meta.env.VITE_API_MOCK === '1'
const MOCK_SESSION_KEY = 'nsentry_mock_session'

function readMockSession(): AuthUser | null {
  try {
    const raw = localStorage.getItem(MOCK_SESSION_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

function writeMockSession(user: AuthUser | null): void {
  try {
    if (user) localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(user))
    else localStorage.removeItem(MOCK_SESSION_KEY)
  } catch {
    // localStorage unavailable (private mode, SSR) — mock session is best-effort
  }
}

function mockUserFromEmail(email: string): AuthUser {
  return { id: `mock-${email}`, email, displayName: email.split('@')[0] }
}

export const auth = {
  async signIn(email: string, password: string): Promise<ApiResponse<{ user: AuthUser }>> {
    if (MOCK_MODE) {
      if (!email.includes('@')) {
        return { error: { message: 'Enter a valid email address', status: 400 } }
      }
      if (password.length < 8) {
        return { error: { message: 'Password must be at least 8 characters', status: 400 } }
      }
      const user = mockUserFromEmail(email)
      writeMockSession(user)
      return { data: { user } }
    }
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      })
      const json = await response.json()
      if (!response.ok) return { error: { message: json.error || 'Sign in failed', status: response.status } }
      return { data: { user: json.user } }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' } }
    }
  },

  async signUp(
    email: string,
    password: string,
    _options?: { displayName?: string },
  ): Promise<ApiResponse<{ user: AuthUser }>> {
    if (MOCK_MODE) {
      if (!email.includes('@')) {
        return { error: { message: 'Enter a valid email address', status: 400 } }
      }
      if (password.length < 8) {
        return { error: { message: 'Password must be at least 8 characters', status: 400 } }
      }
      const user = mockUserFromEmail(email)
      writeMockSession(user)
      return { data: { user } }
    }
    try {
      // /api/auth/signup proxies the SaaS gateway's POST /v1/signup and sets
      // the session cookie from the returned JWT — signup lands signed-in.
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name: _options?.displayName }),
      })
      const json = await response.json()
      if (!response.ok) return { error: { message: json.error || 'Sign up failed', status: response.status } }
      return { data: { user: json.user } }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' } }
    }
  },

  async signOut(): Promise<ApiResponse<void>> {
    setSaasToken(null)
    if (MOCK_MODE) {
      writeMockSession(null)
      return { data: undefined }
    }
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
      return { data: undefined }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' } }
    }
  },

  async getUser(): Promise<ApiResponse<AuthUser>> {
    if (MOCK_MODE) {
      const user = readMockSession()
      if (!user) return { error: { message: 'Not authenticated', status: 401 } }
      return { data: user }
    }
    try {
      const response = await fetch('/api/auth/session', { credentials: 'include' })
      const json = await response.json()
      if (!response.ok || !json.authenticated) {
        setSaasToken(null)
        return { error: { message: 'Not authenticated', status: response.status } }
      }
      // Capture the raw session JWT so saas-client can send it as a Bearer
      // header cross-origin to api.sentry.nself.org (the cookie won't travel).
      if (typeof json.token === 'string') setSaasToken(json.token)
      return { data: json.user }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' } }
    }
  },
}

export const isMockMode = MOCK_MODE

const api = { auth }
export default api
